import { CustomWindow } from '@utils/models/customwindow';
import { useBlockMedia } from "./video-block";

declare let window: CustomWindow;

// Stage 2: Initialize YouTube
export const useVideoBlockYouTube = () => {
  useBlockMedia().initializeConsentCheck(document.querySelectorAll(".video-block__content--youtube"));

  document.addEventListener('YouTubeScriptLoaded', function() {
    // Stage 4: Initialize YouTube iframe API
    if (window.Cookiebot.consent.marketing && !!window.KnowitOptimizelyFramework.YouTube) {
      document.querySelectorAll(".video-block__content--youtube").forEach(element => {
        const blockElement = element as HTMLElement;

        // If this script is already initialized
        if (blockElement.dataset.initializedMedia === "true") {
          return;
        }

        blockElement.classList.remove("video-block__content--prevent-video");
        blockElement.classList.remove("video-block__content--show-video");

        // Set initialized attribute to prevent this script from running again
        blockElement.dataset.initializedMedia = "true";
      });

      window.KnowitOptimizelyFramework.YouTube.initialize();
    }
  });

  // When CookieBot consents are changed
  window.addEventListener('CookiebotOnAccept', function() {
    document.dispatchEvent(new CustomEvent("YouTubeRelatedScriptLoaded"));
  });

  // Tell other modules that this script is loaded
  document.dispatchEvent(new CustomEvent("YouTubeRelatedScriptLoaded"));

  // Stage 5: Add a YouTube-player and start playing when the user clicks on a video block
  const initializeYouTubeMediaBlocks = () => {
    document.querySelectorAll(".video-block__content--youtube").forEach(element => {
      const blockElement = element as HTMLElement;

      // If this script is already initialized
      if (blockElement.dataset.initializedMediaBlockEvents === "true") {
        return;
      }

      blockElement.addEventListener("click", () => {
        initializeVideoAndPlay(blockElement);
      });

      blockElement.dataset.initializedMediaBlockEvents = "true";
    });
  };

  const initializeVideoAndPlay = (blockElement: Element) => {
    if (
      blockElement instanceof HTMLElement &&
      !!window.YT &&
      !!window.Cookiebot?.consent?.marketing
    ) {
      const { videoid, containerid } = blockElement.dataset;

      if (!blockElement.querySelector("iframe")) {
        blockElement?.classList.remove("video-block__content--show-video");
        blockElement.classList.add("video-block__content--load-video");

        const spinnerElement = blockElement?.querySelector(".video-block__loading");
        spinnerElement?.classList.remove("hidden");
        const loadingAlert = blockElement?.querySelector(".video-block__hidden-alert") as HTMLDivElement;
        if (loadingAlert) {
          loadingAlert.textContent = loadingAlert.dataset.text as string;
        }

        const player = new window.YT.Player(containerid, {
          videoId: videoid,
          playerVars: {
            playsinline: 1,
            rel: 0,
          },
          events: {
            onReady: (event: YT.PlayerEvent) => {
              event.target.mute();
              event.target.playVideo();
            },
            onStateChange: (event: YT.OnStateChangeEvent) => {
              if (event.data === window.YT.PlayerState.PLAYING) {
                blockElement.classList.remove("video-block__content--load-video");
                blockElement.classList.add("video-block__content--show-video");
                spinnerElement?.classList.add("hidden");
                if (loadingAlert) {
                  loadingAlert.textContent = "";
                }
              }
            },
          },
        });

        window.KnowitOptimizelyFramework?.YouTube?.players.push({
          ytPlayerId: containerid,
          player: player,
        });
      } 
      else {
        // Player already exists, just play it
        const existing = window.KnowitOptimizelyFramework?.YouTube?.players.find(
          x => x.ytPlayerId === containerid
        );
        existing?.player?.playVideo();
      }
    }
  };

  return {
    initializeYouTubeMediaBlocks,
  };
};

// This method is called from YouTube-api
window.onYouTubeIframeAPIReady = () => {
  useVideoBlockYouTube().initializeYouTubeMediaBlocks();
};